import * as STD from '@emersya/mpa-std';
import * as BasketNS from '@emersya/mpa-basket';
import * as CM from '@emersya/mpa-components-manager';
import {
	Field,
	InputSelect,
	Block,
	Text
} from '@emersya/mpa-components';

// import { Field, Image, InputSelect } from '@libs/MPAComponents/index';

export interface IPlannerFloorEditorComponentConfiguration extends Field.Configuration {
	allFree: boolean;
	label?: STD.translationsManager.TranslationMapping | null;
};

export class PlannerFloorEditorComponent extends Field.Component {
	readonly allFree: boolean;
	protected inputSelect!: InputSelect.Component;

	static override initialize() {
		super.initialize();
	};

	async updateOptions(event: STD.events.Event<any, CM.Component<any, any>>) {
		const currentProduct = app.basket?.basket.currentProduct;
		if (!app.basket || !currentProduct) {
			this.component_throwLibraryError('', {});
		}
		const floorPart = currentProduct.getPart(['floor']);
		floorPart.options.length = 0;
		floorPart.currentOption = null;
		const inputSelect = this.getInputSelect();
		for (const c of Array.from(inputSelect.component_outerChilds)) {
			await c.component_delete();
		}
		const updatedProductSKU = app.currentProduct?.sku?.value;
		if (!updatedProductSKU) {
			this.component_throwUserError('Error while loading new sku product from basket', {});
		}
		const PMSCurrentProduct = app.component_configuration.PMS.products.find(x => x.SKU === currentProduct.id);
		if (!PMSCurrentProduct) {
			this.component_throwUserError('Missing PMS product for current product', {});
		}
		;
		const floorConfiguration = PMSCurrentProduct.configurations.find(x => (
			((x.context.forProducts.length === 1 && x.context.forProducts[0] === '*') || (x.context.forProducts.includes(updatedProductSKU))) &&
			x.SKU === '@floor'
		));
		if (!floorConfiguration) {
			this.component_throwUserError('No configuration with @floor SKU!', {});
		}
		const relatedOptions = floorConfiguration.context.options;
		await app.fillComponentOptions(
			this,
			floorPart,
			inputSelect,
			relatedOptions,
			floorConfiguration,
			'product-optionTile',
			event,
			this.allFree
		);
		for (const fakeTitle of inputSelect.component_querySelectorAll('.fake-title')) {
			fakeTitle.remove();
		}
		STD.translationsManager.update(this.component_DOM);
	};

	async reset(event: STD.events.Event<any, CM.Component<any, any>>) {
		await this.updateOptions(event);
		if (!app.currentProduct?.reference?.value) {
			this.component_throwLibraryError('', {});
		}
		const defaultForProductObject = app.currentDefaults['@floor'];
		// const defaultFloorSKU = app.currentDefaults.floor;
		if (defaultForProductObject) {
			const select = this.getInputSelect();
			await select.updateValueFromId(defaultForProductObject);
		}
	};

	protected async createDOM() {
		await this.component_find('@:content').component_instantiateOuterChild({
			type: 'input-select',
			isSlot: false,
			id: 'options',
			slot: null,
			tree: this.component_tree,
			parentNode: null,
			configuration: {}
		});
	};

	protected getInputSelect() {
		return <InputSelect.Component>this.component_find('@:content.options');
	}

	override async onValueUpdated(ev: STD.events.Event<any, CM.Component<any, any>>) {
		await super.onValueUpdated(ev);
		try {
			app.setAppUnusable();
			const select = this.getInputSelect();
			const option = select.value;
			if (!app.basket || !app.basket.basket.currentProduct) {
				this.component_throwLibraryError('', {});
			}
			const floorPart = app.basket.basket.currentProduct.getPart(['floor']);
			if (!option) {
				if (floorPart.currentOption) {
					await floorPart.unuseOption();
				}
			} else {
				if (floorPart.currentOption && floorPart.currentOption.id === option.id) {
					return;
				}
				await floorPart.useOption(option.id);
				if (!floorPart.currentOption) {
					this.component_throwLibraryError('', {});
				}
				const volumeMethod = floorPart.currentOption.pricingInfo?.volumeComputationMethod?.boundTo || null;
				if (volumeMethod === 'unit') {
					await floorPart.currentOption.updateVolume(1);
				} else if (volumeMethod === 'm2') {
					await floorPart.currentOption.updateVolume(app.getWDH().depth * app.getWDH().width);
				}
			}
		} catch (error: any) {
			this.component_throwBoundError('Cannot update floor options', {
				event: ev
			}, error);
		} finally {
			app.setAppUsable();
		}
	};

	protected async initializeProduct(product: BasketNS.Product) {
		const configuration = app.component_configuration.PMS.products.find(x => x.SKU === product.id)?.
		configurations.
		find(x => x.SKU === '@floor');
		const part = product.createPart({
			id: 'floor',
			title: 'Floor',
			reference: null,
			metaData: null,
			parts: [],
			options: [],
			pricing: configuration && configuration.pricing ? BasketNS.Pricing.deserialize(<BasketNS.Pricing.FinalPricing.ISerialized & BasketNS.Pricing.BoundPricing.ISerialized>configuration.pricing) : null
		});
		await part.initialize();
	};

	override async component_initialize() {
		await this.createDOM();
		await super.component_initialize();
		if (!app.basket) {
			this.component_throwLibraryError('', {});
		}
		for (const product of app.basket.basket.products) {
			await this.initializeProduct(product);
		}
	};

	private async onNewProduct(event: STD.events.Event<any, CM.Component<any, any>>) {
		const product = event.details.product;
		await this.initializeProduct(product);
	};

	constructor(
		descriptor: ConstructorParameters<typeof CM.Component>[0],
		configuration: IPlannerFloorEditorComponentConfiguration
	) {
		if (configuration.label) {
			configuration['@innerConfigurations'] = {
				header: <Block.Configuration>{
					'@innerConfigurations': {
						label: <Text.Configuration>{
							'@translations': {
								text: configuration.label
							}
						}
					}
				}
			};
		}
		super(descriptor, configuration);
		this.allFree = configuration.allFree;
		this.component_DOM = <HTMLDivElement>STD.DOM.factory({
			tag: 'div'
		});
		void this.onNewProduct;
	};
};
export namespace NS {
	export const Component = PlannerFloorEditorComponent;
	export type Component = PlannerFloorEditorComponent;
	export type Configuration = IPlannerFloorEditorComponentConfiguration;
}
import manifestXMLSource from './PlannerFloorEditor.xml';
import './PlannerFloorEditor.scss';

CM.Manager.declare({
	id: 'viewer-planner-floor-editor',
	type: PlannerFloorEditorComponent,
	manifest: manifestXMLSource
});
